import { createStore, get, set, del } from 'idb-keyval';
import toast from 'react-hot-toast';
import { logger } from './logger';
import { secureIdbStorage, fallbackSecureStorage } from './secureIdbStorage';

const zustandStore = createStore('zustand-db', 'auth-store');

// Enhanced IndexedDB storage with fallback and better error handling
export const idbStorage = {
  getItem: async <T>(name: string): Promise<T | null> => {
    try {
      // Try secure storage first
      const secureData = await secureIdbStorage.getItem<T>(name);
      if (secureData !== null) {
        return secureData;
      }

      // Fallback to regular IndexedDB
      const data = await get<T>(name, zustandStore);
      return data ?? null;
    } catch (error) {
      logger.error('IndexedDB getItem failed', {
        name,
        error: error instanceof Error ? error.message : error
      });

      // Try fallback storage
      try {
        return await fallbackSecureStorage.getItem<T>(name);
      } catch {
        toast.error(`Storage: Failed to get item "${name}"`);
        return null;
      }
    }
  },

  setItem: async <T>(name: string, value: T): Promise<void> => {
    try {
      // Store in secure storage
      await secureIdbStorage.setItem(name, value);

      // Also store in regular IndexedDB for compatibility
      await set(name, value, zustandStore);
    } catch (error) {
      logger.error('IndexedDB setItem failed', {
        name,
        error: error instanceof Error ? error.message : error
      });

      // Try fallback storage
      try {
        await fallbackSecureStorage.setItem(name, value);
      } catch {
        toast.error(`Storage: Failed to set item "${name}"`);
      }
    }
  },

  removeItem: async (name: string): Promise<void> => {
    try {
      // Remove from both storages
      await secureIdbStorage.removeItem(name);
      await del(name, zustandStore);
    } catch (error) {
      logger.error('IndexedDB removeItem failed', {
        name,
        error: error instanceof Error ? error.message : error
      });

      // Try fallback storage
      try {
        fallbackSecureStorage.removeItem(name);
      } catch {
        toast.error(`Storage: Failed to remove item "${name}"`);
      }
    }
  },

  // Additional secure storage methods
  secureGetItem: async <T>(name: string): Promise<T | null> => {
    try {
      return await secureIdbStorage.getItem<T>(name);
    } catch (error) {
      logger.error('Secure storage getItem failed', {
        name,
        error: error instanceof Error ? error.message : error
      });
      return null;
    }
  },

  secureSetItem: async <T>(name: string, value: T): Promise<void> => {
    try {
      await secureIdbStorage.setItem(name, value);
    } catch (error) {
      logger.error('Secure storage setItem failed', {
        name,
        error: error instanceof Error ? error.message : error
      });
      throw error;
    }
  },

  secureRemoveItem: async (name: string): Promise<void> => {
    try {
      await secureIdbStorage.removeItem(name);
    } catch (error) {
      logger.error('Secure storage removeItem failed', {
        name,
        error: error instanceof Error ? error.message : error
      });
    }
  },

  // Storage management methods
  clear: async (): Promise<void> => {
    try {
      await secureIdbStorage.clear();
      // Note: We don't clear regular IndexedDB to maintain compatibility
    } catch (error) {
      logger.error('Secure storage clear failed', {
        error: error instanceof Error ? error.message : error
      });
    }
  },

  getStorageInfo: async () => {
    try {
      return await secureIdbStorage.getStorageInfo();
    } catch (error) {
      logger.error('Failed to get storage info', {
        error: error instanceof Error ? error.message : error
      });
      return { totalKeys: 0, totalSize: 0, keys: [] };
    }
  }
};
